import { Box } from '@mui/material';
import { memo, RefObject, useEffect, useRef, useState } from 'react';

import { useSplitScreenResize } from '@/components/layout/SplitScreenLayout/SplitScreenResizeContext';
import { zIndexes } from '@/styles/zIndexes';

import { normalizeSearchText } from './hooks/useParagraphFind';
import { SEARCH_HIGHLIGHT_COLORS } from './searchHighlightPalettes';

interface TextPosition {
  node: Text;
  start: number;
  end: number;
}

const GRAPHEME_SEGMENTER = new Intl.Segmenter(undefined, {
  granularity: 'grapheme',
});

/** Maps normalized literal matches back to PDF text nodes, including split spans. */
export function findPDFTextRanges(layer: Element, query: string): Range[] {
  const needle = normalizeSearchText(query);
  if (!needle) return [];
  let source = '';
  const positions: TextPosition[] = [];
  const walker = document.createTreeWalker(layer, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode();
  while (node) {
    if (node instanceof Text) {
      source += node.data;
      for (let offset = 0; offset < node.length; offset++) {
        positions.push({ node, start: offset, end: offset + 1 });
      }
      // PDF.js marks line endings with a following <br>, not a text node.
      if (
        node.parentElement?.nextElementSibling?.tagName === 'BR' &&
        !source.endsWith(' ')
      ) {
        source += ' ';
        positions.push({ node, start: node.length, end: node.length });
      }
    }
    node = walker.nextNode();
  }
  let text = '';
  const starts: TextPosition[] = [];
  const ends: TextPosition[] = [];
  // Keep combining marks with their base, even across PDF text spans.
  for (const { segment, index } of GRAPHEME_SEGMENTER.segment(source)) {
    // Each source UTF-16 unit has a position, so both segment boundaries exist.
    const first = positions[index]!;
    const last = positions[index + segment.length - 1]!;
    for (const character of segment.normalize('NFKC')) {
      const next = /\s/u.test(character) ? ' ' : character;
      if (next === ' ' && text.endsWith(' ')) continue;
      text += next;
      // Normalization and lowercase can expand a glyph, such as ﬁ or İ.
      for (let i = 0; i < next.toLowerCase().length; i++) {
        starts.push(first);
        ends.push(last);
      }
    }
  }
  // Lowercase the complete text so contextual letters, such as sigma, agree
  // with paragraph and query normalization.
  text = text.toLowerCase();
  const ranges: Range[] = [];
  for (
    let start = text.indexOf(needle);
    start !== -1;
    start = text.indexOf(needle, start + needle.length)
  ) {
    const first = starts[start];
    const last = ends[start + needle.length - 1];
    if (!first || !last) continue;
    const range = document.createRange();
    range.setStart(first.node, first.start);
    range.setEnd(last.node, last.end);
    ranges.push(range);
  }
  return ranges;
}

interface HighlightRegion {
  id?: string;
  pageNumber: number;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface TextHighlight extends HighlightRegion {
  selected: boolean;
}

interface CachedRanges {
  query: string;
  ranges: Range[];
}

function isTextLayerNode(node: Node) {
  return (
    node instanceof Element &&
    (node.matches('.textLayer') || !!node.querySelector('.textLayer'))
  );
}

/** Removes cached matches for changed text layers. Returns true if any layer changed. */
function forgetChangedLayers(
  cache: WeakMap<Element, CachedRanges>,
  mutations: MutationRecord[]
) {
  let changed = false;
  for (const mutation of mutations) {
    const target =
      mutation.target instanceof Element
        ? mutation.target
        : mutation.target.parentElement;
    const layer = target?.closest('.textLayer');
    if (layer) {
      cache.delete(layer);
      changed = true;
    } else if (
      [...mutation.addedNodes, ...mutation.removedNodes].some(isTextLayerNode)
    ) {
      changed = true;
    }
  }
  return changed;
}

/**
 * Joins the rectangles of one match into one rectangle for each text line.
 * PDF.js gives each span and space its own rectangle, and these can overlap or
 * differ in height. Overlapping fills blend into darker slivers.
 */
export function mergeLineRects(rects: DOMRect[]): DOMRect[] {
  const lines: DOMRect[] = [];
  const visible = rects.filter((r) => r.width > 0 && r.height > 0);
  for (const rect of visible.sort((a, b) => a.left - b.left)) {
    // Rectangles are on one line when they share at least half their height.
    const line = lines.find(
      (l) =>
        Math.min(l.bottom, rect.bottom) - Math.max(l.top, rect.top) >=
        Math.min(l.height, rect.height) / 2
    );
    if (!line) {
      lines.push(new DOMRect(rect.x, rect.y, rect.width, rect.height));
      continue;
    }
    const left = Math.min(line.left, rect.left);
    const top = Math.min(line.top, rect.top);
    line.width = Math.max(line.right, rect.right) - left;
    line.height = Math.max(line.bottom, rect.bottom) - top;
    line.x = left;
    line.y = top;
  }
  return lines;
}

function contains(region: HighlightRegion, x: number, y: number) {
  return (
    x >= region.left &&
    x <= region.left + region.width &&
    y >= region.top &&
    y <= region.top + region.height
  );
}

/**
 * Uses the rendered text layer for word geometry; scans keep paragraph highlights.
 * Pass regions whose identity changes only when the matches, page geometry, or
 * set of nearby pages changes. Each change measures every match on those pages.
 */
export const ParagraphFindTextHighlights = memo(
  function ParagraphFindTextHighlights({
    anchor,
    query,
    regions,
    selectedParagraphId,
  }: {
    anchor: RefObject<HTMLDivElement | null>;
    query: string;
    regions: HighlightRegion[];
    selectedParagraphId?: string;
  }) {
    const [rects, setRects] = useState<TextHighlight[]>([]);
    const isResizing = useSplitScreenResize();
    // Matches for each text layer. A mutation inside a layer removes its entry,
    // so a new scroll window or selection only measures the cached ranges.
    const rangeCache = useRef(new WeakMap<Element, CachedRanges>());
    // Increments when PDF.js adds, removes, or rescales a text layer.
    const [layerVersion, setLayerVersion] = useState(0);
    const observerRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
      const container = anchor.current?.parentElement;
      if (!container) return;
      let frame = 0;
      const observer = new MutationObserver((mutations) => {
        const changed = forgetChangedLayers(rangeCache.current, mutations);
        if (!changed) return;
        // PDF.js renders a page in several mutation batches. Measure once per frame.
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => setLayerVersion((v) => v + 1));
      });
      observerRef.current = observer;
      observer.observe(container, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style'],
      });
      return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        observerRef.current = null;
      };
    }, [anchor]);

    useEffect(() => {
      const origin = anchor.current;
      const container = origin?.parentElement;
      if (!origin || !container || !query.trim() || !regions.length) {
        setRects((current) => (current.length ? [] : current));
        return;
      }
      // The viewer scales the canvas and existing overlays during a panel drag.
      // Measure new text only after it returns to unscaled document coordinates.
      if (isResizing) return;
      // React can change the text layer in this commit before the observer runs.
      const pending = observerRef.current?.takeRecords() ?? [];
      forgetChangedLayers(rangeCache.current, pending);
      const bounds = origin.getBoundingClientRect();
      const highlights: TextHighlight[] = [];
      for (const pageNumber of new Set(regions.map((r) => r.pageNumber))) {
        const layer = container.querySelector(
          `[data-pdf-page-number="${pageNumber}"] .textLayer`
        );
        if (!layer) continue;
        let cached = rangeCache.current.get(layer);
        if (cached?.query !== query) {
          cached = { query, ranges: findPDFTextRanges(layer, query) };
          rangeCache.current.set(layer, cached);
        }
        const pageRegions = regions.filter((r) => r.pageNumber === pageNumber);
        for (const range of cached.ranges) {
          for (const rect of mergeLineRects(
            Array.from(range.getClientRects())
          )) {
            const left = rect.left - bounds.left;
            const top = rect.top - bounds.top;
            const x = left + rect.width / 2;
            const y = top + rect.height / 2;
            // Prefer the selected paragraph when OCR regions overlap.
            const paragraph =
              pageRegions.find(
                (r) =>
                  !!selectedParagraphId &&
                  r.id === selectedParagraphId &&
                  contains(r, x, y)
              ) ?? pageRegions.find((r) => contains(r, x, y));
            if (!paragraph) continue;
            highlights.push({
              pageNumber,
              selected:
                !!selectedParagraphId && paragraph.id === selectedParagraphId,
              left,
              top,
              width: rect.width,
              height: rect.height,
            });
          }
        }
      }
      setRects(highlights);
    }, [anchor, query, regions, selectedParagraphId, layerVersion, isResizing]);

    return rects.map(({ pageNumber, selected, ...geometry }, index) => (
      <Box
        key={index}
        data-testid="paragraph-find-text-highlight"
        data-page-number={pageNumber}
        data-selected={selected}
        // Inline geometry: sx would add a CSS class for each unique rectangle.
        style={geometry}
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          backgroundColor: SEARCH_HIGHLIGHT_COLORS.wordFill,
          mixBlendMode: 'multiply',
          zIndex: zIndexes.HIGHLIGHT,
        }}
      />
    ));
  }
);
